import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import {
  CommentV4,
  ConversationV4,
  ConversationsV4Response,
  PostCommentV4Request,
  PostCommentV4Response
} from '@core/models/comments-v4.model';
import { GbeEndPointV4 } from '@core/models/gbe/gbe-endpoint.enum';
import { GBE_BASE_URL } from '@core/tokens/gbe-url.token';

/**
 * Client for the GBE V4 comments/conversations API. Comments are grouped into
 * conversations scoped to an (entity_type, entity_uid) pair. For timeline steps
 * we use entity_type = 'ROUTE_ACTION' and entity_uid = the route action's uid.
 *
 * Request bodies are camelCase (the request interceptor converts to snake_case);
 * query params are written snake_case here because they are not auto-converted.
 */
@Injectable({ providedIn: 'root' })
export class CommentsV4Service {
  private readonly http = inject(HttpClient);
  private readonly gbeUrl = inject(GBE_BASE_URL);

  private buildUrl(endpoint: string): string {
    return `${this.gbeUrl}/${endpoint}`;
  }

  getConversations$(p: {
    entityType: string;
    entityUids: string[];
    dataspaceId?: number;
    planId?: string;
    includeResolved?: boolean;
  }): Observable<ConversationV4[]> {
    const body: {
      entityType: string;
      entityUids: string[];
      planId?: string;
      includeResolved?: boolean;
    } = { entityType: p.entityType, entityUids: p.entityUids };
    if (p.planId) {
      body.planId = p.planId;
    }
    if (p.includeResolved !== undefined) {
      body.includeResolved = p.includeResolved;
    }
    const params = this.dataspaceParams(p.dataspaceId);
    return this.http
      .post<ConversationsV4Response>(
        this.buildUrl(`${GbeEndPointV4.COMMENTS}/conversations/search`),
        body,
        { params }
      )
      .pipe(map((r) => r.conversations));
  }

  postComment$(
    body: PostCommentV4Request,
    dataspaceId?: number
  ): Observable<PostCommentV4Response> {
    const params = this.dataspaceParams(dataspaceId);
    return this.http.post<PostCommentV4Response>(
      this.buildUrl(GbeEndPointV4.COMMENTS),
      body,
      {
        params
      }
    );
  }

  editComment$(
    commentId: string,
    message: string,
    dataspaceId?: number
  ): Observable<CommentV4> {
    const params = this.dataspaceParams(dataspaceId);
    return this.http.patch<CommentV4>(
      this.buildUrl(`${GbeEndPointV4.COMMENTS}/${commentId}`),
      { message },
      { params }
    );
  }

  deleteComment$(commentId: string, dataspaceId?: number): Observable<void> {
    const params = this.dataspaceParams(dataspaceId);
    return this.http.delete<void>(this.buildUrl(`${GbeEndPointV4.COMMENTS}/${commentId}`), {
      params
    });
  }

  setResolved$(
    conversationId: string,
    resolved: boolean,
    dataspaceId?: number
  ): Observable<ConversationV4> {
    const params = this.dataspaceParams(dataspaceId);
    return this.http.patch<ConversationV4>(
      this.buildUrl(`${GbeEndPointV4.COMMENTS}/conversations/${conversationId}/resolved`),
      { resolved },
      { params }
    );
  }

  getCommentableTypes$(dataspaceId?: number): Observable<{ entityTypes: string[] }> {
    const params = this.dataspaceParams(dataspaceId);
    return this.http.get<{ entityTypes: string[] }>(
      this.buildUrl(`${GbeEndPointV4.COMMENTS}/commentable-types`),
      { params }
    );
  }

  addCommentableType$(dataspaceId: number, entityType: string): Observable<void> {
    const params = this.dataspaceParams(dataspaceId);
    return this.http.post<void>(
      this.buildUrl(`${GbeEndPointV4.COMMENTS}/commentable-types`),
      { entityType },
      { params }
    );
  }

  removeCommentableType$(dataspaceId: number, entityType: string): Observable<void> {
    const params = this.dataspaceParams(dataspaceId);
    return this.http.delete<void>(
      this.buildUrl(`${GbeEndPointV4.COMMENTS}/commentable-types/${entityType}`),
      { params }
    );
  }

  private dataspaceParams(dataspaceId?: number): HttpParams {
    let params = new HttpParams();
    if (dataspaceId !== undefined) {
      params = params.set('data_space_id', String(dataspaceId));
    }
    return params;
  }
}
